import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Card,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBackIosNew";
import StoreIcon from "@mui/icons-material/StorefrontOutlined";
import PeopleIcon from "@mui/icons-material/PeopleAltOutlined";
import ReceiptIcon from "@mui/icons-material/ReceiptLongOutlined";
import PaidIcon from "@mui/icons-material/PaidOutlined";
import { getHttpClient } from "src/services/rest-api";
import { HTTPService } from "src/services/httpservice";

interface Overview {
  company: any;
  subscription: any;
  invoices: any[];
  locations: any[];
  users: any[];
  audit: any[];
  notifications: any[];
  crm: any[];
  analytics: any[];
  stats: {
    invoice_count: number;
    locations_count: number;
    users_count: number;
    paid_total: number;
    analytics_count: number;
    currency: string | null;
  };
}

const fmtDate = (d: string | null) =>
  d ? new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—";

const fmtDateTime = (d: string | null) =>
  d ? new Date(d).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—";

const statusColor: Record<string, "success" | "warning" | "error" | "default"> = {
  ACTIVE: "success",
  PAID: "success",
  PENDING: "warning",
  PAST_DUE: "warning",
  CANCELLED: "error",
  FAILED: "error",
  VOID: "error",
};

const SectionCard: React.FC<{ title: string; count?: number; children: React.ReactNode }> = ({ title, count, children }) => (
  <Card variant="outlined" sx={{ borderRadius: "16px", p: 3, bgcolor: "#fff", height: "100%" }}>
    <Box display="flex" alignItems="center" gap={1} mb={1.5}>
      <Typography fontSize="15px" fontWeight={700}>{title}</Typography>
      {count !== undefined && (
        <Chip label={count} size="small" sx={{ height: 20, fontSize: "11px", fontWeight: 700, bgcolor: "#EEF2FF", color: "#4338CA" }} />
      )}
    </Box>
    <Divider sx={{ mb: 2 }} />
    {children}
  </Card>
);

const Empty = ({ text }: { text: string }) => (
  <Typography fontSize="13px" color="text.secondary" py={1}>{text}</Typography>
);

const StatCard: React.FC<{ label: string; value: React.ReactNode; icon: React.ReactNode; color: string }> = ({ label, value, icon, color }) => (
  <Card variant="outlined" sx={{ borderRadius: "14px", p: 2.5, bgcolor: "#fff", height: "100%" }}>
    <Box display="flex" justifyContent="space-between" alignItems="flex-start">
      <Box>
        <Typography fontSize="12px" color="text.secondary" fontWeight={600} textTransform="uppercase">{label}</Typography>
        <Typography fontSize="24px" fontWeight={800} mt={0.5} letterSpacing="-0.5px">{value}</Typography>
      </Box>
      <Box sx={{ width: 36, height: 36, borderRadius: "10px", display: "flex", alignItems: "center", justifyContent: "center", bgcolor: `${color}1A`, color }}>
        {icon}
      </Box>
    </Box>
  </Card>
);

const Customer360Page: React.FC = () => {
  const { companyUuid } = useParams<{ companyUuid: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<Overview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchOverview = useCallback(async () => {
    if (!companyUuid) return;
    setLoading(true);
    setError(null);
    try {
      const res = await getHttpClient(`${HTTPService.APPADMIN_COMPANY_OVERVIEW}/${companyUuid}/overview`, "GET", null, null, null);
      setData(res.data?.data || null);
    } catch (err: any) {
      setError(err?.data?.message || "Failed to load customer overview.");
    } finally {
      setLoading(false);
    }
  }, [companyUuid]);

  useEffect(() => {
    fetchOverview();
  }, [fetchOverview]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={10}><CircularProgress /></Box>
    );
  }

  if (error || !data) {
    return (
      <Box textAlign="center" py={10}>
        <Typography color="text.secondary">{error || "No data found."}</Typography>
      </Box>
    );
  }

  const { company, subscription, invoices, locations, users, audit, notifications, crm, analytics, stats } = data;
  const cur = stats.currency || subscription?.currency || "";
  const isTrial = company?.trial_status === "ACTIVE";

  return (
    <Box display="flex" flexDirection="column" gap={3}>
      {/* Header */}
      <Box display="flex" alignItems="center" gap={1.5}>
        <Tooltip title="Back to companies">
          <IconButton size="small" onClick={() => navigate("/companies")} sx={{ border: "1px solid #E2E8F0", borderRadius: "10px" }}>
            <ArrowBackIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
        <Box>
          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
            <Typography fontSize="22px" fontWeight={800} letterSpacing="-0.5px">
              {company?.company_name || "Company"}
            </Typography>
            {company?.disabled && <Chip label="Disabled" size="small" color="error" />}
            {isTrial && <Chip label="Trial" size="small" color="warning" />}
            {subscription?.status && <Chip label={subscription.status} size="small" color={statusColor[subscription.status] || "default"} />}
          </Box>
          <Typography fontSize="13px" color="text.secondary">
            {company?.company_email || "—"} · {company?.company_country || "—"} · joined {fmtDate(company?.createdAt)}
          </Typography>
        </Box>
      </Box>

      {/* Stat row */}
      <Grid container spacing={2}>
        <Grid item xs={6} md={3}>
          <StatCard label="Plan" value={<span style={{ fontSize: 18 }}>{subscription?.plan_name || company?.plan_type || (isTrial ? "Trial" : "—")}</span>} icon={<PaidIcon sx={{ fontSize: 18 }} />} color="#6366F1" />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard label="Locations" value={stats.locations_count} icon={<StoreIcon sx={{ fontSize: 18 }} />} color="#0EA5E9" />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard label="Users" value={stats.users_count} icon={<PeopleIcon sx={{ fontSize: 18 }} />} color="#10B981" />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard label="Paid to date" value={<span style={{ fontSize: 18 }}>{cur} {stats.paid_total.toFixed(2)}</span>} icon={<ReceiptIcon sx={{ fontSize: 18 }} />} color="#F59E0B" />
        </Grid>
      </Grid>

      <Grid container spacing={2.5}>
        {/* Subscription */}
        <Grid item xs={12} md={6}>
          <SectionCard title="Subscription">
            {subscription ? (
              <Stack spacing={1.25}>
                {[
                  ["Plan", subscription.plan_name],
                  ["Status", subscription.status],
                  ["Billing cycle", subscription.billing_cycle],
                  ["Price", `${subscription.currency || ""} ${subscription.price ?? "—"}`],
                  ["Started", fmtDate(subscription.started_at || subscription.createdAt)],
                  ["Next billing", fmtDate(subscription.next_billing_at)],
                  ["Provider", subscription.provider],
                ].map(([k, v]) => (
                  <Box key={k as string} display="flex" justifyContent="space-between">
                    <Typography fontSize="13px" color="text.secondary" fontWeight={600}>{k}</Typography>
                    <Typography fontSize="13px" fontWeight={600}>{v || "—"}</Typography>
                  </Box>
                ))}
              </Stack>
            ) : (
              <Empty text={isTrial ? "On free trial — no paid subscription yet." : "No subscription on record."} />
            )}
            {isTrial && (
              <Box mt={2} p={1.5} sx={{ bgcolor: "#FFFBEB", borderRadius: "10px" }}>
                <Typography fontSize="12px" color="#92400E" fontWeight={600}>
                  Trial expires {fmtDate(company?.trial_expires_at)}
                </Typography>
              </Box>
            )}
            {company?.custom_price != null && company?.custom_price !== "" && (
              <Box mt={2} p={1.5} sx={{ bgcolor: "#EEF2FF", borderRadius: "10px" }}>
                <Typography fontSize="12px" color="#4338CA" fontWeight={700}>
                  Custom price: {company.custom_currency || ""} {Number(company.custom_price).toFixed(2)}/store
                </Typography>
                {company.custom_price_note && (
                  <Typography fontSize="11px" color="text.secondary" mt={0.25}>{company.custom_price_note}</Typography>
                )}
              </Box>
            )}
          </SectionCard>
        </Grid>

        {/* Invoices */}
        <Grid item xs={12} md={6}>
          <SectionCard title="Invoices" count={stats.invoice_count}>
            {invoices.length === 0 ? (
              <Empty text="No invoices yet." />
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ "& th": { fontSize: "11px", fontWeight: 700, color: "text.secondary", py: 0.5 } }}>
                    <TableCell>Number</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell align="right">Total</TableCell>
                    <TableCell align="right">Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invoices.map((inv) => (
                    <TableRow key={inv.invoice_uuid || inv.invoice_number}>
                      <TableCell sx={{ fontSize: "12px", fontFamily: "monospace" }}>{inv.invoice_number}</TableCell>
                      <TableCell sx={{ fontSize: "12px" }}>{fmtDate(inv.paid_at || inv.createdAt)}</TableCell>
                      <TableCell align="right" sx={{ fontSize: "12px", fontWeight: 600 }}>{inv.currency} {Number(inv.total).toFixed(2)}</TableCell>
                      <TableCell align="right">
                        <Chip label={inv.status} size="small" color={statusColor[inv.status] || "default"} sx={{ height: 18, fontSize: "10px", fontWeight: 700 }} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </SectionCard>
        </Grid>

        {/* Locations */}
        <Grid item xs={12} md={6}>
          <SectionCard title="Locations" count={stats.locations_count}>
            {locations.length === 0 ? (
              <Empty text="No locations." />
            ) : (
              <Stack divider={<Divider />} spacing={0}>
                {locations.map((loc, i) => (
                  <Box key={loc.location_uuid || i} display="flex" justifyContent="space-between" py={1}>
                    <Typography fontSize="13px" fontWeight={600}>{loc.name || `Location ${i + 1}`}</Typography>
                    <Typography fontSize="12px" color="text.secondary">{loc.city || loc.country || ""}</Typography>
                  </Box>
                ))}
              </Stack>
            )}
          </SectionCard>
        </Grid>

        {/* Users */}
        <Grid item xs={12} md={6}>
          <SectionCard title="Users" count={stats.users_count}>
            {users.length === 0 ? (
              <Empty text="No users." />
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ "& th": { fontSize: "11px", fontWeight: 700, color: "text.secondary", py: 0.5 } }}>
                    <TableCell>Name</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell align="right">Email</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.map((u, i) => (
                    <TableRow key={u.email || i}>
                      <TableCell sx={{ fontSize: "12px", fontWeight: 600 }}>{u.manager_name || u.username || "—"}</TableCell>
                      <TableCell sx={{ fontSize: "12px" }}>{u.role || "—"}</TableCell>
                      <TableCell align="right" sx={{ fontSize: "12px", color: "text.secondary" }}>{u.email || "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </SectionCard>
        </Grid>

        {/* CRM */}
        <Grid item xs={12} md={6}>
          <SectionCard title="CRM / Leads" count={crm.length}>
            {crm.length === 0 ? (
              <Empty text="No CRM activity for this email." />
            ) : (
              <Stack divider={<Divider />} spacing={0}>
                {crm.map((c, i) => (
                  <Box key={c.id || i} py={1}>
                    <Box display="flex" justifyContent="space-between">
                      <Typography fontSize="13px" fontWeight={600}>{c.name || c.email}</Typography>
                      <Chip label={c.pipeline_status || c.status || "—"} size="small" sx={{ height: 18, fontSize: "10px", fontWeight: 600 }} />
                    </Box>
                    <Typography fontSize="12px" color="text.secondary">{c.type || "Contact"} · {fmtDate(c.createdAt)}</Typography>
                  </Box>
                ))}
              </Stack>
            )}
          </SectionCard>
        </Grid>

        {/* Notifications */}
        <Grid item xs={12} md={6}>
          <SectionCard title="Notifications" count={notifications.length}>
            {notifications.length === 0 ? (
              <Empty text="No notifications sent." />
            ) : (
              <Stack divider={<Divider />} spacing={0}>
                {notifications.map((n, i) => (
                  <Box key={n.notification_uuid || i} py={1}>
                    <Box display="flex" justifyContent="space-between" gap={1}>
                      <Typography fontSize="13px" fontWeight={600} noWrap>{n.title}</Typography>
                      <Typography fontSize="11px" color="text.disabled" whiteSpace="nowrap">{fmtDateTime(n.createdAt)}</Typography>
                    </Box>
                    {n.body && <Typography fontSize="12px" color="text.secondary">{n.body}</Typography>}
                  </Box>
                ))}
              </Stack>
            )}
          </SectionCard>
        </Grid>

        {/* Audit */}
        <Grid item xs={12} md={6}>
          <SectionCard title="Audit Trail">
            {audit.length === 0 ? (
              <Empty text="No audit entries." />
            ) : (
              <Stack divider={<Divider />} spacing={0}>
                {audit.map((a, i) => (
                  <Box key={a.id || i} display="flex" justifyContent="space-between" gap={1} py={0.85}>
                    <Typography fontSize="12px" fontWeight={600}>{a.action}</Typography>
                    <Typography fontSize="11px" color="text.disabled" whiteSpace="nowrap">{fmtDateTime(a.createdAt)}</Typography>
                  </Box>
                ))}
              </Stack>
            )}
          </SectionCard>
        </Grid>

        {/* Analytics */}
        <Grid item xs={12} md={6}>
          <SectionCard title="Recent Activity" count={stats.analytics_count}>
            {analytics.length === 0 ? (
              <Empty text="No tracked events." />
            ) : (
              <Stack divider={<Divider />} spacing={0}>
                {analytics.map((e, i) => (
                  <Box key={e.event_uuid || i} display="flex" justifyContent="space-between" gap={1} py={0.85}>
                    <Box display="flex" alignItems="center" gap={1} minWidth={0}>
                      <Chip label={e.category} size="small" sx={{ height: 18, fontSize: "10px", fontWeight: 600 }} />
                      <Typography fontSize="12px" fontWeight={600} noWrap>{e.event_name}</Typography>
                    </Box>
                    <Typography fontSize="11px" color="text.disabled" whiteSpace="nowrap">{fmtDateTime(e.createdAt)}</Typography>
                  </Box>
                ))}
              </Stack>
            )}
          </SectionCard>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Customer360Page;
